import { AxiosError, AxiosResponse } from 'axios'
import { REDUX } from 'constants/'
import { ROUTES_NAMES } from 'constants/servicenow.routes'
import { Dispatch } from 'redux'
import { axiosServiceNowApi } from 'services/Api'
import store from 'store'
import {
    LocationAction,
    LocationList,
    LocationSector,
    ResultLocationGetAll,
    ResultLocationGetAllPaginate,
    ResultLocationGetBySectorId,
    ResultLocationGetWithNotification
} from 'types/location'

let activeLocationsCache: LocationList | null = null
let activeLocationsRequest: Promise<LocationList> | null = null

const LocationActions = {
    /**
     * Obtem uma lista de Locations
     * @param dispatch
     * @param uNotifica omitido/'all' = todas; true = u_notifica=true; false = u_notifica=false
     */
    getAllLocations: (
        dispatch: Dispatch<LocationAction>,
        uNotifica?: boolean | 'all'
    ) => {
        let locationsList = store?.getState()?.locationReducers.locationsList
        const route = ROUTES_NAMES.location.get.all(uNotifica)
        const shouldFetch =
            locationsList.length === 0 || uNotifica !== undefined

        if (shouldFetch) {
            dispatch({
                type: REDUX.reducers.GET_ALL_LOCATIONS_COUNT_LOADING
            } as LocationAction)
            const response: Promise<AxiosResponse<ResultLocationGetAll>> =
                axiosServiceNowApi?.get(route)
            response
                .then((res: AxiosResponse<ResultLocationGetAll>) => {
                    dispatch({
                        type: REDUX.reducers.GET_ALL_LOCATIONS,
                        payload: res.data.result
                    } as LocationAction)
                    dispatch({
                        type: REDUX.reducers.GET_ALL_LOCATIONS_COUNT,
                        payload: res.data.result?.length
                    } as LocationAction)
                })
                .catch((error: AxiosError) => {
                    dispatch({
                        type: REDUX.reducers.ERROR_LOCATIONS,
                        payload: error
                    } as LocationAction)
                })
                .finally(() => {})
        }

        return route
    },
    /**
     * Obtem a quantidade de locations com notificações
     * @param dispatch
     */
    getAllLocationsWithNotifications: async (
        dispatch: Dispatch<LocationAction>
    ) => {
        dispatch({
            type: REDUX.reducers.GET_ALL_LOCATIONS_COUNT_LOADING
        } as LocationAction)
        const response: Promise<
            AxiosResponse<ResultLocationGetWithNotification>
        > = axiosServiceNowApi?.get(
            ROUTES_NAMES.notification.get.count_by_location
        )
        await response
            .then((res: AxiosResponse<ResultLocationGetWithNotification>) => {
                dispatch({
                    type: REDUX.reducers
                        .GET_ALL_LOCATIONS_WITH_NOTICATION_COUNT,
                    payload: res.data.result?.length
                } as LocationAction)
            })
            .catch((error: AxiosError) => {
                dispatch({
                    type: REDUX.reducers.ERROR_LOCATIONS,
                    payload: error
                } as LocationAction)
            })
            .finally(() => {})
        return ROUTES_NAMES.notification.get.count_by_location
    },
    /**
     * Obtem uma lista de Unidades pelo Setor com paginação
     * @param dispatch
     * @param sectorId Id do setor usado para buscar as unidades
     * @param offset Indice atual selecionado na paginação
     * @param limit Limite de itens por pagina
     */
    getLocationsBySectorIdV2: async (
        dispatch: Dispatch<LocationAction>,
        sectorId: string,
        offset?: string,
        limit?: string,
        order?: any,
        locationId?: string
    ) => {
        const response: Promise<AxiosResponse<ResultLocationGetBySectorId>> =
            axiosServiceNowApi?.get(
                ROUTES_NAMES.location.get.by_sector_id_paginate_v2(
                    sectorId,
                    offset,
                    limit,
                    order,
                    locationId
                )
            )
        await response
            .then(
                async (
                    responseLocations: AxiosResponse<ResultLocationGetBySectorId>
                ) => {
                    dispatch({
                        type: REDUX.reducers.GET_LOCATIONS_BY_SECTOR_ID,
                        payload: responseLocations.data.result
                    } as LocationAction)
                }
            )
            .catch((error: AxiosError) => {
                dispatch({
                    type: REDUX.reducers.ERROR_LOCATIONS,
                    payload: error
                } as LocationAction)
            })
            .finally(() => {})
        return ROUTES_NAMES.location.get.by_sector_id_paginate(
            sectorId,
            offset,
            limit
        )
    },
    /**
     * Obtem uma lista de Unidades pelo Setor com paginação
     * @param dispatch
     * @param sectorId Id do setor usado para buscar as unidades
     * @param offset Indice atual selecionado na paginação
     * @param limit Limite de itens por pagina
     */
    getLocationsBySectorId: async (
        dispatch: Dispatch<LocationAction>,
        sectorId: string,
        offset?: string,
        limit?: string,
        order?: any,
        locationId?: string
    ) => {
        const response: Promise<AxiosResponse<ResultLocationGetBySectorId>> =
            axiosServiceNowApi?.get(
                ROUTES_NAMES.location.get.by_sector_id_paginate(
                    sectorId,
                    offset,
                    limit,
                    order,
                    locationId
                )
            )
        await response
            .then(
                async (
                    responseLocations: AxiosResponse<ResultLocationGetBySectorId>
                ) => {
                    for (
                        let index = 0;
                        index < (responseLocations.data.result?.length ?? 0);
                        index++
                    ) {
                        let url =
                            ROUTES_NAMES.user.get.by_location_id_and_profile(
                                responseLocations.data.result[index]
                                    .x_rdsls_notificabe_locations.value,
                                'location_manager'
                            )
                        const responseUserManagers =
                            await axiosServiceNowApi?.get(url)
                        responseLocations.data.result[index].location_managers =
                            responseUserManagers.data.result
                    }

                    dispatch({
                        type: REDUX.reducers.GET_LOCATIONS_BY_SECTOR_ID,
                        payload: responseLocations.data.result
                    } as LocationAction)
                }
            )
            .catch((error: AxiosError) => {
                dispatch({
                    type: REDUX.reducers.ERROR_LOCATIONS,
                    payload: error
                } as LocationAction)
            })
            .finally(() => {})
        return ROUTES_NAMES.location.get.by_sector_id_paginate(
            sectorId,
            offset,
            limit
        )
    },
    /**
     * Envia o objeto LocationSector selecionado para ser atualizado na lista principal de LocationSector
     * @param dispatch
     * @param locationSector
     */
    updateLocationStatusCache: async (
        dispatch: Dispatch<LocationAction>,
        locationSector: LocationSector
    ) => {
        dispatch({
            type: REDUX.reducers.INIT_UPDATE_LOCATION_SECTOR_LIST,
            payload: locationSector
        } as LocationAction)
        return true
    },
    /**
     * Executa ws no serviceNow para trazer as unidades paginadas
     * @param dispatch dispatch padrão para action location
     * @param offset numero atual da pagina solicitada
     * @param limit limite de itens por página
     */
    getAllLocationsPaginate: async (
        dispatch: Dispatch<LocationAction>,
        offset?: string,
        limit?: string,
        order?: any,
        search?: string,
        locationId?: string
    ) => {
        dispatch({
            type: REDUX.reducers.GET_ALL_LOCATIONS_COUNT_LOADING
        } as LocationAction)
        const responseSectorList: Promise<
            AxiosResponse<ResultLocationGetAllPaginate>
        > = axiosServiceNowApi?.get(
            ROUTES_NAMES.location.get.all_paginate(
                offset,
                limit,
                order,
                search,
                locationId
            )
        )
        await Promise.all([responseSectorList])
            .then((promisseResponse: [AxiosResponse<any>]) => {
                dispatch({
                    type: REDUX.reducers.GET_ALL_LOCATIONS_PAGINATE,
                    payload: promisseResponse[0].data?.result
                } as LocationAction)
            })
            .catch((error: AxiosError) => {
                dispatch({
                    type: REDUX.reducers.ERROR_LOCATIONS,
                    payload: error.message
                } as LocationAction)
            })
            .finally(() => {})

        return ROUTES_NAMES.location.get.all_paginate(offset, limit)
    },
    /**
     * Executa ws no serviceNow para trazer a unidade por id
     * @param dispatch dispatch padrão para action location
     * @param id id da unidade
     */
    getById: async (dispatch: Dispatch<LocationAction>, id: string) => {
        dispatch({
            type: REDUX.reducers.GET_ALL_LOCATIONS_COUNT_LOADING
        } as LocationAction)
        const responseSectorList: Promise<
            AxiosResponse<ResultLocationGetAllPaginate>
        > = axiosServiceNowApi?.get(ROUTES_NAMES.location.get.byid(id))
        await Promise.all([responseSectorList])
            .then((promisseResponse: [AxiosResponse<any>]) => {
                dispatch({
                    type: REDUX.reducers.GET_LOCATION_BY_ID,
                    payload: promisseResponse[0].data?.result
                } as LocationAction)
            })
            .catch((error: AxiosError) => {
                dispatch({
                    type: REDUX.reducers.ERROR_LOCATIONS,
                    payload: error.message
                } as LocationAction)
            })
            .finally(() => {})

        return ROUTES_NAMES.location.get.byid(id)
    },
    // Cache próprio: só unidades do Notifica (location.get.all|true)
    getActiveLocations: async () => {
        if (activeLocationsCache) {
            return activeLocationsCache
        }

        if (!activeLocationsRequest) {
            activeLocationsRequest = axiosServiceNowApi
                ?.get(ROUTES_NAMES.location.get.all(true))
                .then((response: AxiosResponse<ResultLocationGetAll>) => {
                    activeLocationsCache = response?.data?.result ?? []
                    return activeLocationsCache
                })
                .catch((error: AxiosError) => {
                    activeLocationsRequest = null
                    console.error('Erro ao buscar unidades ativas:', error)
                    return []
                })
        }

        return activeLocationsRequest ?? []
    },
    resetActiveLocationsCache: () => {
        activeLocationsCache = null
        activeLocationsRequest = null
    }
}

export default LocationActions
